/**
 * Web Push helper — registers the service worker, requests permission,
 * subscribes with VAPID key, posts the subscription to the backend.
 *
 * Usage:
 *   import { enablePushNotifications, disablePushNotifications, getPushStatus } from '@/utils/webPush';
 *   await enablePushNotifications({ user_id, user_type });
 */

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return { supported: true, permission: Notification.permission, subscribed: false };
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!sub,
    endpoint: sub ? sub.endpoint : null,
  };
}

export async function enablePushNotifications({ user_id = null, user_type = 'guest' } = {}) {
  if (!isPushSupported()) {
    throw new Error('Your browser does not support push notifications.');
  }

  // 1) Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was denied.');
  }

  // 2) Ensure service worker is registered (CRA registers it lazily via register())
  let reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) {
    reg = await navigator.serviceWorker.register('/service-worker.js');
  }
  await navigator.serviceWorker.ready;

  // 3) Fetch VAPID public key
  const keyRes = await fetch(`${API}/api/push/vapid-public-key`);
  if (!keyRes.ok) throw new Error('Failed to fetch VAPID public key');
  const { public_key } = await keyRes.json();

  // 4) Subscribe (idempotent — returns existing if already subscribed)
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }

  // 5) Send subscription to backend
  const body = {
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
      auth: arrayBufferToBase64(sub.getKey('auth')),
    },
    user_id: user_id,
    user_type: user_type,
  };
  const res = await fetch(`${API}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Subscribe API failed');
  return sub;
}

export async function disablePushNotifications() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  try {
    await fetch(`${API}/api/push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
  } catch (_) {
    /* best-effort */
  }
  await sub.unsubscribe();
  return true;
}
